import React, { useContext, useRef, useCallback } from "react";
import { isMobile } from "react-device-detect";

import { MachineDto } from "../Models/LogSearchResultDto";
import { MachinesGroupType } from "../Models/MachinesGroupType";
import { useMachinesContext } from "../../../context/MachinesContext";
import { SlackSearchResultsContext } from "../Context/SlackSearchResultsContext";

interface SlackViewMobileMachineCardProps {
  machine: MachineDto;
  groupType: MachinesGroupType;
  isRead: boolean;
  onMachineLeftClick: (machineId: string) => void;
  onMachineRightClick: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    machineId: string,
    position?: { x: number; y: number }
  ) => void;
  handleMenuClose: () => void;
  setReadMachine: (id: string, value: boolean, postCount: number) => void;
}

export const SlackViewMobileMachineCard: React.FC<
  SlackViewMobileMachineCardProps
> = ({
  machine,
  groupType,
  isRead,
  handleMenuClose,
  setReadMachine,
  onMachineRightClick,
  onMachineLeftClick,
}) => {
  const { machineColorMap } = useMachinesContext();
  const { selectedInspector, hasMachineFlag, toggleMachineFlag } = useContext(
    SlackSearchResultsContext
  );

  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const longPressTriggered = useRef<boolean>(false);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    if ((e.target as HTMLElement).closest(".flag-icon")) {
      return;
    }

    longPressTriggered.current = false;
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };

    pressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      onMachineRightClick(
        e as unknown as React.MouseEvent<HTMLDivElement>,
        machine.MachineId,
        touchStartPos.current
      );
    }, 500);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    if (!touchStartPos.current) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = Math.abs(currentX - touchStartPos.current.x);
    const deltaY = Math.abs(currentY - touchStartPos.current.y);

    const moveThreshold = 5;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (pressTimer.current) {
        clearTimeout(pressTimer.current);
        pressTimer.current = null;
      }

      touchStartPos.current = null;
      longPressTriggered.current = false;
    }
  };

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobile) return;
      if (pressTimer.current) {
        clearTimeout(pressTimer.current);
        pressTimer.current = null;
      }

      if (touchStartPos.current && !longPressTriggered.current) {
        onMachineLeftClick(machine.MachineId);
      }

      touchStartPos.current = null;
      longPressTriggered.current = false;
    },
    [machine.MachineId, onMachineLeftClick]
  );

  const handleFlagClick = async (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation();

    if (!longPressTriggered.current) {
      if (selectedInspector) {
        await toggleMachineFlag(machine.MachineId);
      }
    }
  };

  const handleFlagTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    e.stopPropagation();

    longPressTriggered.current = false;

    pressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;

      const mockMouseEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
      } as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>;

      onMachineRightClick(mockMouseEvent, machine.MachineId, {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      });
    }, 500);
  };

  const handleFlagTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    e.stopPropagation();
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
    if (!longPressTriggered.current) {
      handleFlagClick(e);
    }
    longPressTriggered.current = false;
  };

  const showMessageCounter =
    !selectedInspector && groupType !== MachinesGroupType.PastEntries;
  const fullMachineName = `[${machine.MachineCode}] - ${machine.MachineName}`;
  // Machines that have not been assigned a group come back with an empty GroupCode.
  const groupCode = machine.GroupCode?.trim();

  return (
    <div
      className={`slack-card ${isMobile ? "mobile" : "web"}`}
      onClick={
        !isMobile ? () => onMachineLeftClick(machine.MachineId) : undefined
      }
      onContextMenu={(e) => {
        if (!isMobile) {
          e.preventDefault();
          onMachineRightClick(e, machine.MachineId);
        }
      }}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchMove={isMobile ? handleTouchMove : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
      onTouchCancel={isMobile ? handleTouchEnd : undefined}
      style={{
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        touchAction: "manipulation",
      }}
    >
      <div className="group-name">{groupCode ? groupCode : "—"}</div>
      <span
        className="status-circle"
        style={{
          backgroundColor: machineColorMap[machine.MachineId] ?? "transparent",
        }}
      />
      <div className="machine-name" title={fullMachineName}>
        {fullMachineName}
      </div>
      <div
        className="right-group"
        onClick={(e) => handleFlagClick(e)}
        onTouchStart={(e) => handleFlagTouchStart(e)}
        onTouchEnd={(e) => handleFlagTouchEnd(e)}
        onTouchCancel={(e) => {
          if (pressTimer.current) {
            clearTimeout(pressTimer.current);
          }
        }}
      >
        {selectedInspector &&
          (hasMachineFlag(machine.MachineId) ? (
            <img
              src={require("../../../assets/icons/flag-filled-yellow.png")}
              alt="flag"
              width={25}
              height={25}
              style={{ cursor: "pointer" }}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <img
              src={require("../../../assets/icons/flag-white-empty.png")}
              alt="empty flag"
              width={25}
              height={25}
              style={{ cursor: "pointer" }}
              onContextMenu={(e) => e.preventDefault()}
            />
          ))}
        {showMessageCounter && (
          <div className="counter">{machine.PostCount}</div>
        )}
      </div>
    </div>
  );
};
